import { useCallback, useEffect } from 'react';
import { AnalyticsContext, AnalyticsEventName, NudgeAnalyticsContext } from './constants';
import { useLazyGetItemAnalyticsModelByItemPksQuery } from '@/redux/api/client/itemAnalyticsModel';
import { ConvertItemAnalyticsModelBase } from './analyticsUtils';
import { trackEvent } from './utils';
import { ItemAnalyticsModel } from '@/@types/client-api';
import { trackViewItemEvent } from './ecommerce';
import { runAfterCoreContent } from '@/utils/coreContentReady';

export const useTrackViewItem = (contentCode?: string) => {
  const [fetchItems] = useLazyGetItemAnalyticsModelByItemPksQuery();

  useEffect(() => {
    if (!contentCode) return;
    let cancelled = false;
    let pendingRequest: ReturnType<typeof fetchItems> | undefined;

    const fetchAndTrack = async () => {
      try {
        pendingRequest = fetchItems({
          itemPks: [contentCode],
          populatePrice: true,
          limitPriceCalls: true
        });

        const { data } = await pendingRequest;

        if (cancelled) return;
        if (data && data.length > 0) {
          trackViewItemEvent(data[0]);
        }
      } catch (error) {
        if (cancelled || (error instanceof Error && error.name === 'AbortError')) {
          return;
        }

        throw error;
      } finally {
        pendingRequest = undefined;
      }
    };

    const cancelDeferredFetch = runAfterCoreContent(fetchAndTrack);
    return () => {
      cancelled = true;
      pendingRequest?.abort?.();
      if (typeof cancelDeferredFetch === 'function') {
        cancelDeferredFetch();
      }
    };
  }, [contentCode, fetchItems]);
};

const trackContentsEvent = (
  eventName: AnalyticsEventName,
  context?: AnalyticsContext,
  itemAnalyticsModel?: ItemAnalyticsModel
) => {
  if (context && itemAnalyticsModel) {
    trackEvent({
      eventName,
      parameters: {
        context: context || '',
        items: [ConvertItemAnalyticsModelBase(itemAnalyticsModel)]
      }
    });
  }
};

export const trackPdpDownloadContentsEvent = (
  context?: AnalyticsContext,
  itemAnalyticsModel?: ItemAnalyticsModel
) => trackContentsEvent(AnalyticsEventName.DownloadContents, context, itemAnalyticsModel);

export const trackPubUpdateInfoContentsEvent = (
  context?: AnalyticsContext,
  itemAnalyticsModel?: ItemAnalyticsModel
) => trackContentsEvent(AnalyticsEventName.PubUpdateInfo, context, itemAnalyticsModel);

export const trackSearchContentsContentsEvent = (
  context?: AnalyticsContext,
  itemAnalyticsModel?: ItemAnalyticsModel
) => trackContentsEvent(AnalyticsEventName.SearchContents, context, itemAnalyticsModel);
export const useTrackViewCrossLink = () => {
  return useCallback((context?: AnalyticsContext) => {
    trackEvent({
      eventName: AnalyticsEventName.ViewCrossLink,
      parameters: {
        context: context || ''
      }
    });
  }, []);
};

export const trackDownloadScheduleEvent = () => {
  trackEvent({
    eventName: AnalyticsEventName.DownloadSchedule,
    parameters: {}
  });
};

export const trackMemSuitePdpEvent = (name?: string) => {
  if (name) {
    trackEvent({
      eventName: AnalyticsEventName.MemSuitePdp,
      parameters: {
        name
      }
    });
  }
};

export const trackFollowEvent = (
  context?: NudgeAnalyticsContext,
  itemAnalyticsModel?: ItemAnalyticsModel
) => {
  const item = ConvertItemAnalyticsModelBase(itemAnalyticsModel);

  if (context && itemAnalyticsModel) {
    trackEvent({
      eventName: AnalyticsEventName.Follow,
      parameters: {
        context: context || '',
        ...(item && { items: [item] })
      }
    });
  }
};
